import React from "react";
import { useNavigate } from "react-router-dom";
import { Download, Keyboard, KeyRound, LogOut, Mail, Moon, ShieldCheck, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useToast } from "@/context/ToastContext";
import { usePWA } from "@/context/PWAContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** Round avatar button with account actions, theme and sign-out */
export function UserMenu({ side = "bottom" }: { side?: "right" | "bottom" }) {
  const { user, logout, hasPermission } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { canInstall, installApp } = usePWA();
  const { success } = useToast();
  const navigate = useNavigate();

  const initials = user?.FirstName ? `${user.FirstName[0]}${user.LastName ? user.LastName[0] : ""}`.toUpperCase() : "U";
  const roleName = user?.UserRole?.RoleName || "Administrator";
  const dark = resolvedTheme === "dark";

  const handleLogout = async () => {
    await logout();
    success("Logged out", "You have safely signed out of your session.");
    navigate("/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="mt-1 rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <Avatar className="size-8">
            <AvatarFallback className="bg-gold text-xs font-bold text-sidebar">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={side === "right" ? "end" : "end"} sideOffset={10} className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold">
              {user?.FirstName} {user?.LastName || ""}
            </p>
            <p className="truncate text-xs text-muted-foreground">{user?.Email}</p>
            <div className="pt-1">
              <Badge variant="blue" className="text-[10px] font-normal">
                {roleName}
              </Badge>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(hasPermission("email.read") || hasPermission("email.send")) && (
          <DropdownMenuItem onClick={() => navigate("/emails?tab=settings")} className="cursor-pointer">
            <Mail className="mr-2 size-4" />
            Email connection
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/change-password")} className="cursor-pointer">
          <KeyRound className="mr-2 size-4" />
          Change password
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings/preferences?tab=security")} className="cursor-pointer">
          <ShieldCheck className="mr-2 size-4" />
          Security and devices
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("crm:show-shortcuts"))} className="cursor-pointer">
          <Keyboard className="mr-2 size-4" />
          Keyboard shortcuts
          <kbd className="ml-auto rounded border border-border bg-muted px-1.5 font-mono text-[10px]">?</kbd>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer">
          {dark ? <Sun className="mr-2 size-4" /> : <Moon className="mr-2 size-4" />}
          {dark ? "Light mode" : "Dark mode"}
        </DropdownMenuItem>
        {canInstall && (
          <DropdownMenuItem onClick={installApp} className="cursor-pointer">
            <Download className="mr-2 size-4" />
            Install app
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-danger focus:bg-danger-soft focus:text-danger">
          <LogOut className="mr-2 size-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
